import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useModalContext } from '@fourthwall/components';
import { useContinueBarVisibility } from '@modules/Dashboard/LaunchStrategy/hooks/useContinueBarVisibility';
import { useLaunchStrategyProgress } from '../hooks/useLaunchStrategyProgress';
import { useLaunchStrategyData } from '../hooks/useLaunchStrategyData';
import { useLaunchStrategyVisibility } from '../hooks/useLaunchStrategyVisibility';
import { getAbsoluteGuideIndex } from '../utils/getAbsoluteGuideIndex';
import { LaunchStrategyModal } from '../components/LaunchStrategyModal';
const LaunchStrategyContext = createContext(undefined);
export const LaunchStrategyProvider = ({ children }) => {
    const { open, close } = useModalContext();
    const data = useLaunchStrategyData();
    const { visible, onDismiss } = useLaunchStrategyVisibility();
    const progress = useLaunchStrategyProgress();
    const { visible: showContinueBar, setVisible: setShowContinueBar } = useContinueBarVisibility();
    const totalGuides = data.modal.categories.reduce((acc, category) => acc + category.guides.length, 0);
    const absoluteActiveGuideIndex = useMemo(() => getAbsoluteGuideIndex(data.modal.categories, progress.category.index, progress.guide.index), [progress.category.index, data.modal.categories, progress.guide.index]);
    const handleModalClose = useCallback((absoluteActiveGuideIndex) => {
        if (absoluteActiveGuideIndex !== totalGuides - 1) {
            setShowContinueBar(true);
        }
        close();
    }, [totalGuides, close, setShowContinueBar]);
    const openModal = useCallback(() => {
        setShowContinueBar(false);
        open(LaunchStrategyModal, {
            data: data.modal,
            initialGuideIndex: progress.guide.index,
            initialCategoryIndex: progress.category.index,
            totalGuides,
            onClose: handleModalClose,
            onProgressChange: (categoryIndex, guideIndex) => {
                progress.category.setIndex(categoryIndex);
                progress.guide.setIndex(guideIndex);
            },
        });
    }, [
        setShowContinueBar,
        open,
        data.modal,
        progress.guide,
        progress.category,
        totalGuides,
        handleModalClose,
    ]);
    const value = useMemo(() => ({
        data,
        visible,
        showContinueBar,
        progress,
        absoluteActiveGuideIndex,
        totalGuides,
        openModal,
        setShowContinueBar,
        onDismiss,
    }), [
        data,
        visible,
        showContinueBar,
        progress,
        absoluteActiveGuideIndex,
        totalGuides,
        openModal,
        setShowContinueBar,
        onDismiss,
    ]);
    return React.createElement(LaunchStrategyContext.Provider, { value: value }, children);
};
export const useLaunchStrategyContext = () => {
    const context = useContext(LaunchStrategyContext);
    if (context === undefined) {
        throw new Error('useLaunchStrategy must be used within a LaunchStrategyProvider');
    }
    return context;
};
